import React, { useEffect, useMemo, useRef } from 'react';
import {
  Badge, Box, Button, Flex, IconButton, Menu, MenuButton, MenuItem, MenuList, Portal,
  Text, Tooltip, useToast, useColorModeValue,
} from '@chakra-ui/react';
import { FiBell } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { useQueryClient } from 'react-query';
import useUnreadSummary, { UnreadThread } from '../hooks/useUnreadSummary';
import useNotifications from '../hooks/useNotifications';
import useCurrentUser from '../hooks/useCurrentUser';
import {
  AppNotification, NotificationType, NOTIFICATION_TAG, NOTIFICATION_SCHEME,
} from '../models/notification';

// item 355a / 482 — the global notification center: ONE bell in the persistent strip, visible on
// every page and through a collapsed sidebar. Two groups: MESSAGES (chat unread, per-thread, from
// useUnreadSummary) + ALERTS (discrete event notifications — new commission, task assigned, extra
// pay, invoice finalized — from useNotifications, "until read"). Merged here so there's one place
// to look (item 482 retired the separate sidebar feed). Combined badge, one "Mark all read".
const threadTarget = (t: UnreadThread): string => {
  if (t.kind === 'accountant') return '/accountant';
  if (t.kind === 'team') return '/'; // the Team channel card lives on the dashboard
  // item 398 — help desks live INSIDE their project (routing to /project/helpdesk:x 404'd)
  if (t.kind === 'helpdesk') return t.projectId ? `/project/${t.projectId}` : '/';
  return `/project/${t.threadKey}`;
};

const NotificationBell: React.FC = () => {
  const history = useHistory();
  const toast = useToast();
  const {
    isAdmin, isAccountant, isSuperAdmin, hasFinancialAccess, isOutsideSales,
  } = useCurrentUser();
  const { threads } = useUnreadSummary(true);
  const {
    notifications, unreadCount: alertUnread, markRead, markAllRead: markAllAlerts,
  } = useNotifications(true);
  const queryClient = useQueryClient();
  const [clearing, setClearing] = React.useState(false);
  const footerRule = useColorModeValue('blackAlpha.100', 'whiteAlpha.200'); // item 958 — light-mode contrast

  // item 850 — an external Outside Sales rep gets NO chat notifications (they have no chat access);
  // their bell surfaces ONLY their own commission / extra-pay alerts (see below).
  const visible = useMemo(
    () => (isOutsideSales
      ? []
      : threads.filter((t) => t.kind !== 'accountant' || isAdmin || isAccountant || isSuperAdmin)),
    [isOutsideSales, threads, isAdmin, isAccountant, isSuperAdmin],
  );
  const chatCount = visible.reduce((sum, t) => sum + t.unread, 0);

  // item 850 — the ONLY alerts an external rep may see: their own commission + extra-pay events.
  const COMMISSION_ALERT_TYPES = new Set<NotificationType>([
    NotificationType.Commission,
    NotificationType.ExtraPayApproved,
    NotificationType.ExtraPaySubmitted,
    NotificationType.ExtraPayDeclined,
  ]);

  // item 594 — money-restricted roles (Team / Executive) never see FINANCIAL alerts
  // (payment received/failed/refunded, invoice-to-enter, payroll-entered). These are
  // server-scoped to an accountant/admin audience already; this is a defensive UI filter
  // so a stray financial alert can never surface on a no-money seat.
  const FINANCIAL_ALERT_TYPES = new Set<NotificationType>([
    NotificationType.PaymentFailed,
    NotificationType.PaymentRefunded,
    NotificationType.PaymentReceived,
    NotificationType.InvoiceFinalized,
    NotificationType.ExtraPayEntered,
  ]);
  let alerts: AppNotification[];
  if (isOutsideSales) alerts = notifications.filter((n) => COMMISSION_ALERT_TYPES.has(n.type));
  else if (hasFinancialAccess) alerts = notifications;
  else alerts = notifications.filter((n) => !FINANCIAL_ALERT_TYPES.has(n.type));
  const visibleAlertUnread = (isOutsideSales || !hasFinancialAccess)
    ? alerts.filter((n) => !n.isRead).length
    : alertUnread;
  const count = chatCount + visibleAlertUnread;

  // item 396/482 — one click clears BOTH: every behind chat thread + every unread alert
  const markAllRead = async () => {
    setClearing(true);
    try {
      const apiUrl = process.env.REACT_APP_BASIX_API || '';
      await Promise.all(threads.map((t) => axios.post(
        `${apiUrl}chatread`,
        null,
        { params: t.kind === 'accountant' ? {} : { threadKey: t.threadKey } },
      )));
      queryClient.setQueryData('unreadSummary', []);
      queryClient.invalidateQueries('unreadSummary');
      queryClient.invalidateQueries('chatRead');
      if (alertUnread > 0) await markAllAlerts();
      toast({ status: 'success', title: 'All caught up.', duration: 2500 });
    } catch {
      // item 397 — this failed SILENTLY before (an API restart mid-click); now it says so
      toast({
        status: 'error', title: 'Could not mark as read — try again.', duration: 5000, isClosable: true,
      });
    } finally {
      setClearing(false);
    }
  };

  const openAlert = async (n: AppNotification) => {
    if (!n.isRead) { try { await markRead(n.id); } catch { /* best-effort */ } }
    if (n.link) history.push(n.link);
  };

  // toast when CHAT unread rises (a genuinely new message) — first sample is baseline so opening
  // the app never dings for history; alerts surface in-list, they don't toast
  const prev = useRef<number | null>(null);
  useEffect(() => {
    const prior = prev.current;
    prev.current = chatCount;
    if (prior === null || chatCount <= prior) return;
    const loudest = visible[0];
    toast({
      title: loudest ? `New message — ${loudest.label}` : 'New chat message',
      status: 'info',
      duration: 5000,
      isClosable: true,
      position: 'top-right',
    });
  }, [chatCount, visible, toast]);

  // combined unread count in the tab title so a backgrounded tab still signals
  useEffect(() => {
    const base = document.title.replace(/^\(\d+\)\s*/, '');
    document.title = count > 0 ? `(${count}) ${base}` : base;
  }, [count]);

  const hasAny = visible.length > 0 || alerts.length > 0;

  return (
    <Menu>
      <Box position="relative" flexShrink={0}>
        <MenuButton
          as={IconButton}
          aria-label={count > 0 ? `${count} unread notifications` : 'No unread notifications'}
          title="Notifications"
          icon={<FiBell />}
          size="sm"
          variant="ghost"
        />
        {count > 0 && (
          <Badge
            position="absolute"
            top="-4px"
            right="-4px"
            borderRadius="full"
            px={1.5}
            fontSize="0.6rem"
            colorScheme="red"
            pointerEvents="none"
          >
            {count > 99 ? '99+' : count}
          </Badge>
        )}
      </Box>
      <Portal>
        <MenuList fontSize="sm" maxW="340px" zIndex={1600} py={0}>
          {!hasAny && (
            <Box px={3} py={2}>
              <Text fontSize="sm" opacity={0.6}>You&apos;re all caught up.</Text>
            </Box>
          )}
          {/* item 1359 (Chad: "16 notifications but I can only see 8 — no scroll, no expand") —
              EVERY alert renders inside a scroll region capped to the viewport; the footer
              stays pinned below it. The old hard cap of 8 silently hid the rest. */}
          <Box maxH="min(70vh, 560px)" overflowY="auto" pt={1}>

            {/* MESSAGES — chat unread by thread */}
            {visible.length > 0 && (
            <Text px={3} pt={2} pb={1} fontSize="0.6rem" fontWeight="bold" letterSpacing="0.14em" opacity={0.5}>MESSAGES</Text>
            )}
            {visible.map((t) => (
              <MenuItem key={t.threadKey ?? 'accountant'} onClick={() => history.push(threadTarget(t))}>
                <Flex align="center" w="full" sx={{ gap: '8px' }}>
                  <Text flex={1} noOfLines={1}>{t.label}</Text>
                  <Badge colorScheme="red" borderRadius="full" px={2}>{t.unread}</Badge>
                </Flex>
              </MenuItem>
            ))}

            {/* ALERTS — discrete event notifications, until read */}
            {alerts.length > 0 && (
            <Text px={3} pt={2} pb={1} fontSize="0.6rem" fontWeight="bold" letterSpacing="0.14em" opacity={0.5}>
              {`ALERTS · ${alerts.length}${alertUnread > 0 && alertUnread !== alerts.length ? ` (${alertUnread} unread)` : ''}`}
            </Text>
            )}
            {alerts.map((n) => (
              <MenuItem key={n.id} onClick={() => openAlert(n)}>
                {/* item 593 — hover reveals ALL the valuable info (full title + body + time); the
                  row itself stays a tidy one-liner. */}
                <Tooltip
                  placement="left"
                  hasArrow
                  openDelay={150}
                  label={(
                    <Box maxW="260px">
                      <Text fontWeight="semibold">{n.title}</Text>
                      {n.body && <Text fontSize="xs" mt={1}>{n.body}</Text>}
                      <Text fontSize="0.6rem" opacity={0.7} mt={1}>{new Date(n.createdDate).toLocaleString()}</Text>
                    </Box>
                )}
                >
                  <Flex align="flex-start" w="full" sx={{ gap: '8px' }} opacity={n.isRead ? 0.55 : 1}>
                    {!n.isRead && <Box w="6px" h="6px" mt="6px" borderRadius="full" bg="red.400" flexShrink={0} />}
                    <Box flex={1} minW={0} ml={n.isRead ? '14px' : 0}>
                      <Badge colorScheme={NOTIFICATION_SCHEME[n.type]} fontSize="0.5rem" textTransform="lowercase">
                        {`[${NOTIFICATION_TAG[n.type]}]`}
                      </Badge>
                      <Text noOfLines={1} fontWeight={n.isRead ? 'normal' : 'semibold'}>{n.title}</Text>
                    </Box>
                  </Flex>
                </Tooltip>
              </MenuItem>
            ))}
          </Box>

          {hasAny && (
            <Box px={3} py={2} borderTopWidth="1px" borderColor={footerRule}>
              <Button size="xs" variant="ghost" w="full" isLoading={clearing} onClick={markAllRead} isDisabled={count === 0}>
                Mark all as read
              </Button>
            </Box>
          )}
        </MenuList>
      </Portal>
    </Menu>
  );
};

export default NotificationBell;
